import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * Settings — minimal account profile page mirroring the design philosophy
 * of the other pages (header bar + section card + simple form). Loads the
 * current user from /auth/me on mount; saves to PUT /auth/me.
 *
 * Email is read-only (it's the auth identity; changing it is a Supabase
 * re-verification flow we don't expose here).
 */
export default function Settings() {
  const { user } = useAuth();
  const initials = ((user?.firstName || user?.email?.[0] || 'U')[0] + ((user?.lastName || '')[0] || '')).toUpperCase();

  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '' });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  /* Stripe Connect. Kept separate from the profile form on purpose: connecting
   * is a redirect out to Stripe and back, not a field you edit and save, so it
   * must not share the form's dirty/Save state. */
  const [stripe, setStripe] = useState({ loading: true, connected: false, connectedAt: null });
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get('/auth/me')
      .then((res) => {
        const d = res.data?.data || {};
        setForm({
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          phone: d.phone || '',
        });
        setEmail(d.email || '');
        setRole(d.role || '');
        setCreatedAt(d.createdAt || '');
      })
      .catch((err) => {
        // Don't blow up the page on a fetch error — surface a toast instead.
        const msg = err?.response?.data?.message || 'Could not load profile';
        setToast({ kind: 'error', message: msg });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  /* Re-read on focus as well as on mount: connecting happens in a Stripe tab,
   * and the user lands back here with the page already mounted. Without this it
   * still says "Not connected" until a manual reload, which reads as failure. */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/case-manager/stripe/status');
        if (!cancelled) {
          const d = res.data?.data || {};
          setStripe({ loading: false, connected: !!d.connected, connectedAt: d.connectedAt || null });
        }
      } catch (_err) {
        if (!cancelled) setStripe({ loading: false, connected: false, connectedAt: null });
      }
    };
    load();
    window.addEventListener('focus', load);
    return () => { cancelled = true; window.removeEventListener('focus', load); };
  }, []);

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/case-manager/stripe/connect-link');
      const url = res.data?.data?.url;
      if (!url) throw new Error('No link returned');
      // Same tab: Stripe's OAuth screen refuses to render inside an iframe, and
      // a popup is the first thing a blocker kills.
      window.location.href = url;
    } catch (err) {
      setToast({ kind: 'error', message: err?.response?.data?.message || 'Could not start the Stripe connection' });
      setConnecting(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.put('/auth/me', {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
      });
      setToast({ kind: 'success', message: 'Profile saved' });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not save profile';
      setToast({ kind: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    form.firstName !== (user?.firstName || '') ||
    form.lastName !== (user?.lastName || '') ||
    form.phone !== (user?.phone || '');

  const formattedCreatedAt = createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';
  const roleLabel = role
    ? role.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    : '—';

  return (
    <div className="flex flex-col bg-white min-h-screen">
      {/* Header */}
      <div
        className="flex items-center justify-between bg-white border-b border-[#e5e5e5]"
        style={{ height: 60, padding: '14px 24px' }}
      >
        <span className="text-sm font-semibold text-[#0e1e20] leading-[1.5]" style={{ fontFamily: 'Inter Tight' }}>
          Settings
        </span>
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-[#115e59] flex items-center justify-center">
            <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Inter' }}>{initials}</span>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[10000] px-4 py-2 rounded-md shadow-lg text-sm font-medium ${
            toast.kind === 'error' ? 'bg-red-600 text-white' : 'bg-[#115e59] text-white'
          }`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {toast.message}
        </div>
      )}

      {/* Body */}
      <div style={{ padding: '24px 24px 40px 24px' }} className="flex flex-col gap-6 max-w-[820px]">
        {/* Profile card */}
        <section className="rounded-xl border border-[#e5e5e5] bg-white">
          <header className="flex items-center justify-between px-6 py-4 border-b border-[#e5e5e5]">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>Profile</span>
              <span className="text-xs text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>
                Update your personal information. Email is your sign-in and can't be changed here.
              </span>
            </div>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 px-6 py-5">
            <Field label="First Name">
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                disabled={loading}
                className="h-10 px-3 rounded-lg border border-[#e5e7eb] text-sm outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
              />
            </Field>
            <Field label="Last Name">
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                disabled={loading}
                className="h-10 px-3 rounded-lg border border-[#e5e7eb] text-sm outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={email}
                readOnly
                disabled
                className="h-10 px-3 rounded-lg border border-[#e5e7eb] text-sm outline-none bg-[#f9fafb] cursor-not-allowed"
                style={{ fontFamily: 'Inter, sans-serif', color: '#6b7280' }}
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                disabled={loading}
                placeholder="(555) 555-5555"
                className="h-10 px-3 rounded-lg border border-[#e5e7eb] text-sm outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}
              />
            </Field>
            <Field label="Role">
              <div
                className="h-10 px-3 rounded-lg border border-[#e5e7eb] flex items-center bg-[#f9fafb] text-sm"
                style={{ fontFamily: 'Inter, sans-serif', color: '#6b7280' }}
              >
                {roleLabel}
              </div>
            </Field>
            <Field label="Member Since">
              <div
                className="h-10 px-3 rounded-lg border border-[#e5e7eb] flex items-center bg-[#f9fafb] text-sm"
                style={{ fontFamily: 'Inter, sans-serif', color: '#6b7280' }}
              >
                {formattedCreatedAt}
              </div>
            </Field>
          </div>

          <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e5e5e5]">
            <button
              type="button"
              onClick={() => setForm({
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                phone: user?.phone || '',
              })}
              disabled={loading || saving || !dirty}
              className="h-9 px-4 rounded-lg border border-[#e5e7eb] text-sm font-medium text-[#4b5563] hover:bg-gray-50 disabled:opacity-50"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving || !dirty}
              className="h-9 px-4 rounded-lg text-sm font-semibold text-white"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: loading || saving || !dirty ? '#94a3b8' : '#115e59',
              }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </footer>
        </section>

        {/* Payouts. Only shown as connected/not — the account id is never sent
          * to the browser. */}
        <section className="rounded-lg border border-[#e5e7eb] bg-white">
          <div className="px-5 py-4 border-b border-[#f0f0f0]">
            <h2 className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter Tight' }}>
              Payouts
            </h2>
            <p className="mt-0.5 text-xs text-[#6b7280]" style={{ fontFamily: 'Inter' }}>
              Connect a Stripe account to receive your share of the Scale admin fee.
            </p>
          </div>
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div className="text-sm" style={{ fontFamily: 'Inter' }}>
              {stripe.loading ? (
                <span className="text-[#6b7280]">Checking…</span>
              ) : stripe.connected ? (
                <span className="inline-flex items-center gap-2 text-[#0F766E]">
                  <span className="w-2 h-2 rounded-full bg-[#0F766E]" aria-hidden="true" />
                  Connected
                  {stripe.connectedAt && (
                    <span className="text-[#6b7280]">
                      · {new Date(stripe.connectedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  )}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-[#6b7280]">
                  <span className="w-2 h-2 rounded-full bg-[#9ca3af]" aria-hidden="true" />
                  Not connected
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleConnectStripe}
              disabled={connecting || stripe.loading}
              className="h-9 px-4 rounded-lg bg-[#115e59] text-sm font-medium text-white hover:bg-[#0d4a46] disabled:opacity-50"
              style={{ fontFamily: 'Inter' }}
            >
              {connecting ? 'Opening Stripe…' : stripe.connected ? 'Reconnect' : 'Connect with Stripe'}
            </button>
          </div>
        </section>

        <p className="text-xs text-[#9ca3af]" style={{ fontFamily: 'Inter' }}>
          Need to change your email or password? Contact your administrator.
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-[#5b5a64]" style={{ fontFamily: 'Inter' }}>{label}</span>
      {children}
    </label>
  );
}
